import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FaHome, FaCity, FaDollarSign, FaCalendarAlt, FaListAlt } from 'react-icons/fa';
import { fetchAddressSuggestions } from '../mapbox/geocoding';
import './Sidebar.css';
import './Navbar.css';
import './AddProperty.css';
import { showToast } from '../components/toast';

const BASE_URL = "https://api.rside.app";
const todayISO = new Date().toISOString().split('T')[0];
const nextYearISO = (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().split('T')[0]; })();

function AddProperty() {
  const navigate = useNavigate();
  const [initials, setInitials] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Address autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const suggestRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {} // silently fail if user denies
      );
    }
  }, []);

  const handleAddressInput = useCallback((value) => {
    setFormData(prev => ({ ...prev, streetAddress: value }));
    clearTimeout(debounceRef.current);
    if (value.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      const results = await fetchAddressSuggestions(value, userLocation);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    }, 150);
  }, [userLocation]);

  const handleSelectSuggestion = (s) => {
    setFormData(prev => ({ ...prev, streetAddress: s.street, city: s.city, state: s.state, zipCode: s.zip }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handleClick = (e) => { if (suggestRef.current && !suggestRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);



  const [formData, setFormData] = useState({
    name: '',
    propertyType: 'Single Family Home',
    numberOfUnits: 1,
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    notes: '',
    purchasePrice: '',
    renovCost: '',
    downPayment: '',
    mortgagePayment: '',
    hoa: '',
    propertyTaxes: '',
    propertyInsurance: '',
    insuranceStartDate: todayISO,
    insuranceEndDate: nextYearISO,
    units: []
  });

  const isMultiFamily = formData.propertyType === 'Multi Family Home';
  const totalSteps = isMultiFamily ? 4 : 3;
  const totalInvestmentCost = (parseFloat(formData.purchasePrice) || 0) + (parseFloat(formData.renovCost) || 0);
  const fmt = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      setUserEmail(user.email);
      try {
        const res = await fetch(`${BASE_URL}/PropertyManagerName?email=${user.email}`);
        const data = await res.json();
        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));
        setUserFullName(data.Full_Name || '');
      } catch (err) {
        console.error('Error fetching PM info:', err);
      }
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    if (!isMultiFamily) {
      setFormData(prev => ({ ...prev, units: [] }));
      return;
    }
    const unitCount = Math.max(2, parseInt(formData.numberOfUnits) || 2);
    setFormData(prev => {
      const units = [...prev.units];
      while (units.length < unitCount) {
        units.push({ identifier: '', unitName: '', usePropertyAddress: true, streetAddress: '', city: '', state: '', zipCode: '' });
      }
      if (units.length > unitCount) units.length = unitCount;
      return { ...prev, units };
    });
  }, [formData.propertyType, formData.numberOfUnits, isMultiFamily]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'insuranceStartDate' && value) {
      const d = new Date(value);
      d.setFullYear(d.getFullYear() + 1);
      setFormData(prev => ({ ...prev, insuranceStartDate: value, insuranceEndDate: d.toISOString().split('T')[0] }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUnitChange = (i, field, value) => {
    setFormData(prev => {
      const units = [...prev.units];
      units[i] = { ...units[i], [field]: value };
      return { ...prev, units };
    });
  };

  const setPropertyType = (type) => {
    setFormData(prev => ({ ...prev, propertyType: type, numberOfUnits: type === 'Multi Family Home' ? 2 : 1 }));
  };

  const apiErrText = async (res) => {
    try { const j = await res.clone().json(); return j.detail || j.message || JSON.stringify(j); }
    catch { try { return await res.text(); } catch { return res.statusText; } }
  };

  const handleSave = async () => {
    setSaving(true);
    const toMMDDYYYY = (d) => { if (!d) return ''; const [y,m,day] = d.split('-'); return `${m}-${day}-${y}`; };
    try {
      const unitCount = isMultiFamily ? (parseInt(formData.numberOfUnits) || 2) : 1;

      // 1. Add Property (query params)
      const propParams = new URLSearchParams({
        Full_Name: userFullName,
        Email: userEmail,
        Property_Name: formData.name,
        Num_of_Units: unitCount,
        Notes: formData.notes || '',
      });
      if (formData.insuranceStartDate) propParams.set('Insurance_Start', toMMDDYYYY(formData.insuranceStartDate));
      if (formData.insuranceEndDate)   propParams.set('Insurance_End',   toMMDDYYYY(formData.insuranceEndDate));
      const propRes = await fetch(`${BASE_URL}/AddProperty?${propParams}`, {
        method: 'POST',
        headers: { accept: 'application/json' },
      });
      if (!propRes.ok) {
        const msg = await apiErrText(propRes);
        showToast(`Error saving property (${propRes.status}): ${msg}`, 'error');
        setSaving(false); return;
      }
      const addedProp = await propRes.json();
      const propertyName = addedProp.Property_Name || formData.name;

      // 2. Add Doors (query params, all Vacant)
      const doors = isMultiFamily
        ? formData.units.slice(0, unitCount)
        : [{ usePropertyAddress: true, identifier: '1' }];

      for (let i = 0; i < doors.length; i++) {
        const unit = doors[i];
        const unitId = (unit.identifier && unit.identifier.trim())
          ? unit.identifier.trim().slice(0, 5)
          : String.fromCharCode(65 + i);
        const doorParams = new URLSearchParams({
          Property_Name: propertyName,
          Full_Name: userFullName,
          Street_Address: unit.usePropertyAddress ? formData.streetAddress : (unit.streetAddress || formData.streetAddress),
          Zipcode: unit.usePropertyAddress ? formData.zipCode : (unit.zipCode || formData.zipCode),
          City: unit.usePropertyAddress ? formData.city : (unit.city || formData.city),
          State: unit.usePropertyAddress ? formData.state : (unit.state || formData.state),
          Stat: 'Vacant',
          Unit_Number: unitId,
          Email: userEmail,
        });
        const doorRes = await fetch(`${BASE_URL}/AddDoor?${doorParams}`, {
          method: 'POST',
          headers: { accept: 'application/json' },
        });
        if (!doorRes.ok) {
          const msg = await apiErrText(doorRes);
          showToast(`Error adding unit ${unitId} (${doorRes.status}): ${msg}`, 'error');
          setSaving(false); return;
        }
      }

      // 3. Add Financial (query params)
      const finParams = new URLSearchParams({
        Property_Name: propertyName,
        email: userEmail,
        Purchase_Price: Math.round(parseFloat(formData.purchasePrice) || 0),
        Down_Payment: Math.round(parseFloat(formData.downPayment) || 0),
        Total_Cost: Math.round(totalInvestmentCost),
        Renov_Cost: Math.round(parseFloat(formData.renovCost) || 0),
        Mortgage_Payment: Math.round(parseFloat(formData.mortgagePayment) || 0),
        Tax: Math.round(parseFloat(formData.propertyTaxes) || 0),
        Insurance: Math.round(parseFloat(formData.propertyInsurance) || 0),
        HOA: Math.round(parseFloat(formData.hoa) || 0),
      });
      const finRes = await fetch(`${BASE_URL}/AddFinancial?${finParams}`, {
        method: 'POST',
        headers: { accept: 'application/json' },
      });
      if (!finRes.ok) {
        const msg = await apiErrText(finRes);
        showToast(`Error adding financial (${finRes.status}): ${msg}`, 'error');
        setSaving(false); return;
      }

      // 4. Insurance amount + dates
      if (formData.propertyInsurance && parseFloat(formData.propertyInsurance) > 0) {
        const insParams = new URLSearchParams({ property_name: propertyName, email: userEmail });
        insParams.set('insurance_annual', Math.round(parseFloat(formData.propertyInsurance) || 0));
        if (formData.insuranceStartDate) insParams.set('insurance_start', toMMDDYYYY(formData.insuranceStartDate));
        if (formData.insuranceEndDate)   insParams.set('insurance_end',   toMMDDYYYY(formData.insuranceEndDate));
        const ir = await fetch(`${BASE_URL}/UpdateInsuranceDates?${insParams}`, { method: 'PUT' }).catch(e => console.error('Insurance save error:', e));
        if (ir && !ir.ok) console.error('Insurance API error:', ir.status, await ir.text());
      }

      navigate('/properties');
    } catch (err) {
      console.error('Error saving property:', err);
      showToast('Error: ' + (err?.message || 'Could not connect to the server.'), 'error');
    }
    setSaving(false);
  };

  const stepLabel = () => {
    if (step === 1) return 'Property Basics';
    if (isMultiFamily && step === 2) return 'Unit Setup';
    if (step === (isMultiFamily ? 3 : 2)) return 'Financials';
    return 'Review & Save';
  };

  // ── Step 1: Property Basics ──
  const renderStep1 = () => (
    <div className="ap-step-content">
      <div className="ap-type-row">
        <button className={`ap-type-card ${!isMultiFamily ? 'ap-type-selected' : ''}`} onClick={() => setPropertyType('Single Family Home')}>
          <div className={`ap-type-icon-wrap ${!isMultiFamily ? 'ap-type-icon-blue' : ''}`}><FaHome /></div>
          <span className={`ap-type-label ${!isMultiFamily ? 'ap-type-label-blue' : ''}`}>Single Family</span>
          <span className="ap-type-sub">One unit property</span>
        </button>
        <button className={`ap-type-card ${isMultiFamily ? 'ap-type-selected' : ''}`} onClick={() => setPropertyType('Multi Family Home')}>
          <div className={`ap-type-icon-wrap ${isMultiFamily ? 'ap-type-icon-blue' : ''}`}><FaCity /></div>
          <span className={`ap-type-label ${isMultiFamily ? 'ap-type-label-blue' : ''}`}>Multi-Family</span>
          <span className="ap-type-sub">Multiple units</span>
        </button>
      </div>

      <div className="ap-fields-grid">
        <div className="ap-form-field ap-col-2">
          <label>Property Name <span className="ap-req">*</span></label>
          <input name="name" value={formData.name} onChange={handleChange} placeholder="e.g., Sunset Apartments" className="ap-input" />
        </div>
        <div className="ap-form-field ap-col-2" ref={suggestRef} style={{ position: 'relative' }}>
          <label>Street Address <span className="ap-req">*</span></label>
          <input
            name="streetAddress"
            value={formData.streetAddress}
            onChange={e => handleAddressInput(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="123 Main Street"
            className="ap-input"
            autoComplete="off"
          />
          {showSuggestions && (
            <ul className="ap-suggest-list">
              {suggestions.map((s, i) => (
                <li key={i} className="ap-suggest-item" onMouseDown={() => handleSelectSuggestion(s)}>
                  {s.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="ap-form-field">
          <label>City <span className="ap-req">*</span></label>
          <input name="city" value={formData.city} onChange={handleChange} placeholder="City" className="ap-input" />
        </div>
        <div className="ap-form-field">
          <label>State <span className="ap-req">*</span></label>
          <input name="state" value={formData.state} onChange={handleChange} placeholder="State" className="ap-input" />
        </div>
        <div className="ap-form-field">
          <label>Zipcode <span className="ap-req">*</span></label>
          <input name="zipCode" value={formData.zipCode} onChange={handleChange} placeholder="12345" className="ap-input" />
        </div>
        {isMultiFamily && (
          <div className="ap-form-field">
            <label>Number of Units <span className="ap-req">*</span></label>
            <div className="ap-stepper-inline">
              <input
                className="ap-stepper-inline-input"
                type="number"
                min="2"
                value={formData.numberOfUnits}
                onChange={e => setFormData(prev => ({ ...prev, numberOfUnits: Math.max(2, parseInt(e.target.value) || 2) }))}
              />
              <div className="ap-stepper-inline-btns">
                <button type="button" className="ap-stepper-inline-btn" onClick={() => setFormData(prev => ({ ...prev, numberOfUnits: Math.max(2, prev.numberOfUnits - 1) }))}>−</button>
                <div className="ap-stepper-div" />
                <button type="button" className="ap-stepper-inline-btn" onClick={() => setFormData(prev => ({ ...prev, numberOfUnits: prev.numberOfUnits + 1 }))}>+</button>
              </div>
            </div>
          </div>
        )}
        <div className="ap-form-field ap-col-2">
          <label>Notes</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Add any additional notes about this property" className="ap-input ap-textarea" rows={3} />
          <span className="ap-hint">Optional</span>
        </div>
      </div>
    </div>
  );

  // ── Step 2 (MF only): Unit Setup ──
  const renderUnitSetup = () => {
    const propAddrPreview = [formData.streetAddress, formData.city, formData.state, formData.zipCode].filter(Boolean).join(' • ');
    return (
      <div className="ap-step-content">
        <div className="ap-units-web-grid">
          {formData.units.map((unit, i) => {
            const unitAddrPreview = unit.usePropertyAddress
              ? propAddrPreview
              : [unit.streetAddress, unit.city, unit.state, unit.zipCode].filter(Boolean).join(' • ');
            return (
              <div key={i} className="ap-unit-block">
                <div className="ap-unit-block-hd">
                  <span className="ap-unit-num">Unit {i + 1}</span>
                </div>
                <div className="ap-unit-block-body">
                  <div className="ap-form-field">
                    <label>Unit Name</label>
                    <input placeholder="Optional: e.g., The Johnson Unit" value={unit.unitName || ''} onChange={e => handleUnitChange(i, 'unitName', e.target.value)} className="ap-input" />
                    <span className="ap-hint">When using a custom address, the unit identifier is optional</span>
                  </div>
                  <div className="ap-toggle-row">
                    <span>Use property address</span>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={unit.usePropertyAddress} onChange={e => handleUnitChange(i, 'usePropertyAddress', e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  {!unit.usePropertyAddress && (
                    <div className="ap-unit-addr-fields">
                      <input placeholder="Street address" value={unit.streetAddress} onChange={e => handleUnitChange(i, 'streetAddress', e.target.value)} className="ap-input" />
                      <div className="ap-two-col">
                        <input placeholder="City" value={unit.city} onChange={e => handleUnitChange(i, 'city', e.target.value)} className="ap-input" />
                        <input placeholder="State" value={unit.state} onChange={e => handleUnitChange(i, 'state', e.target.value)} className="ap-input" />
                      </div>
                      <input placeholder="Zip" value={unit.zipCode} onChange={e => handleUnitChange(i, 'zipCode', e.target.value)} className="ap-input" />
                    </div>
                  )}
                  {unitAddrPreview && <span className="ap-addr-preview">{unitAddrPreview}</span>}
                  <div className="ap-form-field" style={{ marginTop: 6 }}>
                    <label>Unit Identifier <span className="ap-req">*</span></label>
                    <input placeholder="e.g., Apt A, Unit 1, Upper" value={unit.identifier} maxLength={5} onChange={e => handleUnitChange(i, 'identifier', e.target.value)} className="ap-input" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Financials step ──
  const renderFinancials = () => (
    <div className="ap-step-content">
      <div className="ap-fin-web-grid">
        {/* LEFT: Purchase Details */}
        <div className="ap-fin-section">
          <div className="ap-fin-hd">
            <div className="ap-fin-icon"><FaDollarSign /></div>
            <span className="ap-fin-title">Purchase Details</span>
          </div>
          <div className="ap-form-field">
            <label>Purchase Price <span className="ap-req">*</span></label>
            <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><input name="purchasePrice" type="number" value={formData.purchasePrice} onChange={handleChange} placeholder="150000" className="ap-input ap-input-dl" /></div>
          </div>
          <div className="ap-form-field">
            <label>Renovation Cost + Other Expenses</label>
            <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><input name="renovCost" type="number" value={formData.renovCost} onChange={handleChange} placeholder="0" className="ap-input ap-input-dl" /></div>
            <span className="ap-hint">Repairs, renovations, improvements, or closing fees</span>
          </div>
          <div className="ap-form-field">
            <label>Total Investment Cost</label>
            <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><div className="ap-input ap-input-dl ap-input-ro">{fmt(totalInvestmentCost)}</div></div>
            <span className="ap-hint">Purchase Price + Renovation Cost</span>
          </div>
          <div className="ap-form-field">
            <label>Down Payment</label>
            <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><input name="downPayment" type="number" value={formData.downPayment} onChange={handleChange} placeholder="30000" className="ap-input ap-input-dl" /></div>
          </div>
        </div>

        {/* RIGHT: Monthly + Annual */}
        <div className="ap-fin-right-col">
          <div className="ap-fin-section">
            <div className="ap-fin-hd">
              <div className="ap-fin-icon"><FaCalendarAlt /></div>
              <span className="ap-fin-title">Monthly Expenses</span>
            </div>
            <div className="ap-form-field">
              <label>Mortgage Payment</label>
              <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><input name="mortgagePayment" type="number" value={formData.mortgagePayment} onChange={handleChange} placeholder="1500" className="ap-input ap-input-dl" /></div>
              <span className="ap-hint">Monthly amount</span>
            </div>
            <div className="ap-form-field">
              <label>HOA Fees</label>
              <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><input name="hoa" type="number" value={formData.hoa} onChange={handleChange} placeholder="0" className="ap-input ap-input-dl" /></div>
              <span className="ap-hint">Monthly amount</span>
            </div>
          </div>

          <div className="ap-fin-section">
            <div className="ap-fin-hd">
              <div className="ap-fin-icon"><FaListAlt /></div>
              <span className="ap-fin-title">Annual Expenses</span>
            </div>
            <div className="ap-form-field">
              <label>Property Taxes</label>
              <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><input name="propertyTaxes" type="number" value={formData.propertyTaxes} onChange={handleChange} placeholder="3000" className="ap-input ap-input-dl" /></div>
              <span className="ap-hint">Annual amount</span>
            </div>
            <div className="ap-form-field">
              <label>Insurance</label>
              <div className="ap-dollar-wrap"><span className="ap-dollar">$</span><input name="propertyInsurance" type="number" value={formData.propertyInsurance} onChange={handleChange} placeholder="0" className="ap-input ap-input-dl" /></div>
              <span className="ap-hint">Annual amount</span>
            </div>
            {formData.propertyInsurance && parseFloat(formData.propertyInsurance) > 0 && (
              <div className="ap-date-pair">
                <div className="ap-date-col">
                  <span className="ap-date-lbl">Start Date</span>
                  <input type="date" name="insuranceStartDate" value={formData.insuranceStartDate} onChange={handleChange} className="ap-date-chip" />
                </div>
                <div className="ap-date-col">
                  <span className="ap-date-lbl">End Date</span>
                  <input type="date" name="insuranceEndDate" value={formData.insuranceEndDate} onChange={handleChange} className="ap-date-chip" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Review step ──
  const renderReview = () => {
    const addr = [formData.streetAddress, formData.city, formData.state, formData.zipCode].filter(Boolean).join(', ');
    return (
      <div className="ap-step-content">
        <div className="ap-review-card">
          <p className="ap-review-section-title">Property</p>
          <div className="ap-review-row"><span className="ap-review-lbl">Name</span><span className="ap-review-val">{formData.name || '—'}</span></div>
          <div className="ap-review-row"><span className="ap-review-lbl">Type</span><span className="ap-review-val">{formData.propertyType}</span></div>
          {isMultiFamily && <div className="ap-review-row"><span className="ap-review-lbl">Units</span><span className="ap-review-val">{formData.numberOfUnits}</span></div>}
          <div className="ap-review-row"><span className="ap-review-lbl">Address</span><span className="ap-review-val">{addr || '—'}</span></div>
        </div>
        <div className="ap-review-card">
          <p className="ap-review-section-title">Financials</p>
          <div className="ap-review-row"><span className="ap-review-lbl">Purchase Price</span><span className="ap-review-val">${fmt(parseFloat(formData.purchasePrice) || 0)}</span></div>
          <div className="ap-review-row"><span className="ap-review-lbl">Total Investment</span><span className="ap-review-val">${fmt(totalInvestmentCost)}</span></div>
          <div className="ap-review-row"><span className="ap-review-lbl">Down Payment</span><span className="ap-review-val">${fmt(parseFloat(formData.downPayment) || 0)}</span></div>
          <div className="ap-review-row"><span className="ap-review-lbl">Monthly Mortgage</span><span className="ap-review-val">${fmt(parseFloat(formData.mortgagePayment) || 0)}</span></div>
          <div className="ap-review-row"><span className="ap-review-lbl">Insurance Period</span><span className="ap-review-val">{fmtDate(formData.insuranceStartDate)} – {fmtDate(formData.insuranceEndDate)}</span></div>
        </div>
        {saving && <p className="ap-saving-note">Saving property, please wait…</p>}
      </div>
    );
  };

  const finStep = isMultiFamily ? 3 : 2;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Add Property" />
        <div className="ap-wizard-outer">
          <div className="ap-wizard-card">
            {/* Progress bar */}
            <div className="ap-progress-track">
              <div className="ap-progress-fill" style={{ width: `${(step / totalSteps) * 100}%` }} />
            </div>

            {/* Header */}
            <div className="ap-wiz-header">
              <button className="ap-wiz-nav-btn" onClick={step === 1 ? () => navigate('/properties') : () => setStep(s => s - 1)}>
                {step === 1 ? '✕' : '‹'}
              </button>
              <div className="ap-wiz-title-block">
                <h2 className="ap-wiz-title">{stepLabel()}</h2>
                <span className="ap-wiz-sub">Step {step} of {totalSteps}</span>
              </div>
            </div>

            {/* Body */}
            <div className="ap-wiz-body">
              {step === 1 && renderStep1()}
              {isMultiFamily && step === 2 && renderUnitSetup()}
              {step === finStep && renderFinancials()}
              {step === totalSteps && renderReview()}
            </div>

            {/* Footer */}
            <div className="ap-wiz-footer">
              {step < totalSteps ? (
                <button className="ap-wiz-continue" onClick={() => setStep(s => s + 1)}>Continue →</button>
              ) : (
                <button className="ap-wiz-continue" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Property'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddProperty;
